/**
 * Inbound PO Shipping Confirmation Tracker — single source of truth for
 * stage labels, variants, and operator-allowed transitions.
 *
 * Mirrors the backend constants in
 * `middleware/app/models/po_shipment_confirmation.py` 1:1. Test
 * (`ship-confirmation-stage-meta.test.ts`) pins the contract so future
 * drift between frontend + backend trips a unit test.
 */

import type { ShipConfirmationStage, ShipConfirmationTracker, ShipStatus } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

export const STAGES: readonly ShipConfirmationStage[] = [
  'awaiting_confirmation',
  'confirmed',
  'received',
  'cancelled',
] as const

export const TERMINAL_STAGES: readonly ShipConfirmationStage[] = [
  'received',
  'cancelled',
] as const

/**
 * Kanban board columns — terminals drop off by default (unlike the
 * sibling Freight Pipeline board). This board is about the active "needs
 * follow-up" backlog; once a PO is received or cancelled it's no longer
 * actionable here.
 */
export const BOARD_STAGES: readonly ShipConfirmationStage[] = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const STAGE_LABELS: Record<ShipConfirmationStage, string> = {
  awaiting_confirmation: 'Awaiting Confirmation',
  confirmed: 'Confirmed',
  received: 'Received',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<ShipConfirmationStage, string> = {
  awaiting_confirmation: 'Awaiting Confirmation',
  confirmed: 'Confirmed',
  received: 'Received',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<ShipConfirmationStage, ChipVariant> = {
  awaiting_confirmation: 'warning',
  confirmed: 'success',
  received: 'neutral',
  cancelled: 'danger',
}

/**
 * Operator-driven transitions — mirrors the Python ALLOWED_TRANSITIONS
 * frozenset 1:1 (app/models/po_shipment_confirmation.py): any non-terminal
 * can move to any other stage; either terminal can undo back to
 * `confirmed` or `awaiting_confirmation`. Derived programmatically rather
 * than hand-listed so the table stays in sync with `STAGES` +
 * `TERMINAL_STAGES` if those ever change.
 */
export interface Transition {
  from: ShipConfirmationStage
  to: ShipConfirmationStage
}

const NON_TERMINAL_STAGES = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ...NON_TERMINAL_STAGES.flatMap((from) =>
    STAGES.filter((to) => to !== from).map<Transition>((to) => ({ from, to })),
  ),
  { from: 'received', to: 'confirmed' },
  { from: 'received', to: 'awaiting_confirmation' },
  { from: 'cancelled', to: 'confirmed' },
  { from: 'cancelled', to: 'awaiting_confirmation' },
] as const

export function isTerminal(stage: ShipConfirmationStage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

export function transitionAllowed(
  from: ShipConfirmationStage,
  to: ShipConfirmationStage,
): boolean {
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: ShipConfirmationStage): ShipConfirmationStage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

/**
 * Display status — the stage, except an `awaiting_confirmation` line with
 * at least one logged estimate shows "Estimated Ship Date Logged" (blue). Not a stage:
 * it can't be dragged to and the backend stage machine doesn't know it.
 */
export const SHIP_STATUS_LABELS: Record<ShipStatus, string> = {
  ...STAGE_LABELS,
  estimate_logged: 'Estimated Ship Date Logged',
}

export const SHIP_STATUS_VARIANT: Record<ShipStatus, ChipVariant> = {
  ...STAGE_VARIANT,
  estimate_logged: 'info',
}

export function shipStatusOf(tracker: Pick<ShipConfirmationTracker, 'stage' | 'ship_status' | 'estimate_count'>): ShipStatus {
  if (tracker.ship_status) return tracker.ship_status
  return tracker.stage === 'awaiting_confirmation' && (tracker.estimate_count ?? 0) > 0
    ? 'estimate_logged'
    : tracker.stage
}
